import { Box, Button, Checkbox, List, Modal, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

export type ConsentRelationship = 'Self' | 'Legal guardian';

function getAgreementParagraphs(agreementText: string): string[] {
  return agreementText
    .split(/\n+/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export async function createAgreementPdfBase64(
  agreementText: string,
  signature: string,
  relationship: ConsentRelationship = 'Self'
): Promise<string> {
  const [{ default: pdfMake }, { default: pdfFonts }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? pdfFonts;
  const signedAt = new Date();
  const definition = {
    content: [
      { text: 'Polaris Sleep, P.C.', style: 'practiceName' },
      { text: 'General Informed Consent & Notice of Privacy Practices', style: 'title' },
      { text: 'Last Updated: May 22, 2026', style: 'updated' },
      ...getAgreementParagraphs(agreementText).map((paragraph) => ({ text: paragraph, style: 'body' })),
      {
        unbreakable: true,
        margin: [0, 24, 0, 0],
        stack: [
          { text: 'Electronic Signature', style: 'signatureHeading' },
          { text: signature, style: 'signatureName' },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 230, y2: 0, lineWidth: 1 }] },
          { text: `Signed electronically by ${signature} as ${relationship.toLowerCase()}`, style: 'signatureLabel' },
          { text: `Date: ${signedAt.toLocaleString()}`, style: 'signatureLabel' },
        ],
      },
    ],
    styles: {
      practiceName: { fontSize: 12, bold: true, margin: [0, 0, 0, 4] },
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 16] },
      updated: { fontSize: 8, color: '#666666', margin: [0, 0, 0, 16] },
      body: { fontSize: 9, lineHeight: 1.25, margin: [0, 0, 0, 8] },
      signatureHeading: { fontSize: 10, bold: true, margin: [0, 0, 0, 12] },
      signatureName: { fontSize: 22, italics: true, margin: [0, 0, 0, 4] },
      signatureLabel: { fontSize: 8, color: '#555555', margin: [0, 4, 0, 0] },
    },
    defaultStyle: { fontSize: 9, lineHeight: 1.25 },
    footer: (currentPage: number, pageCount: number) => ({
      text: `Polaris Sleep, P.C.  |  ${currentPage} of ${pageCount}`,
      alignment: 'center',
      fontSize: 8,
      color: '#777777',
      margin: [0, 8, 0, 0],
    }),
  };
  const blob = await new Promise<Blob>((resolve) => {
    pdfMake.createPdf(definition).getBlob(resolve);
  });
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('Unable to encode consent PDF'));
    reader.readAsDataURL(blob);
  });
}

interface InformedConsentModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (signature: string, agreementPdfBase64?: string, relationship?: ConsentRelationship) => void;
  dateOfBirth?: string;
  signedSignature?: string;
  signedRelationship?: ConsentRelationship;
  onDownload?: (agreementPdfBase64: string) => void | Promise<void>;
  submitting: boolean;
  viewOnly?: boolean;
}

export function InformedConsentModal({
  opened,
  onClose,
  onConfirm,
  submitting,
  viewOnly = false,
  signedSignature,
  signedRelationship,
  onDownload,
  dateOfBirth,
}: InformedConsentModalProps): JSX.Element {
  const [consentChecked, setConsentChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [relationship, setRelationship] = useState<ConsentRelationship>('Self');
  const agreementRef = useRef<HTMLDivElement>(null);

  const normalizedDateOfBirth = dateOfBirth?.match(/^\d{2}\/\d{2}\/\d{4}$/)
    ? dateOfBirth.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$1-$2')
    : dateOfBirth;
  const birthDate = normalizedDateOfBirth ? new Date(`${normalizedDateOfBirth}T00:00:00`) : undefined;
  const age =
    birthDate && !Number.isNaN(birthDate.getTime())
      ? Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      : undefined;
  const isMinor = age !== undefined && age < 18;
  const requiresGuardian = isMinor && relationship === 'Self';

  useEffect(() => {
    setRelationship(isMinor ? 'Legal guardian' : 'Self');
  }, [isMinor]);

  const canSubmit = consentChecked && signature.trim().length > 0 && !requiresGuardian && !submitting;

  const handleContinue = async (): Promise<void> => {
    if (canSubmit) {
      try {
        const agreementPdfBase64 = agreementRef.current
          ? await createAgreementPdfBase64(
              agreementRef.current.innerText ?? agreementRef.current.textContent ?? '',
              signature.trim(),
              relationship
            )
          : undefined;
        if (!agreementPdfBase64) {
          throw new Error('PDF content was empty');
        }
        onConfirm(signature.trim(), agreementPdfBase64, relationship);
      } catch (error) {
        console.error('Failed to create informed consent PDF', error);
        showNotification({ color: 'red', message: 'Unable to prepare consent PDF. Please try again.' });
      }
    }
  };

  const handleDownload = async (): Promise<void> => {
    if (!agreementRef.current || !signedSignature || !onDownload) {
      return;
    }
    try {
      const agreementPdfBase64 = await createAgreementPdfBase64(
        agreementRef.current.innerText ?? agreementRef.current.textContent ?? '',
        signedSignature,
        signedRelationship ?? 'Self'
      );
      await onDownload(agreementPdfBase64);
    } catch (error) {
      console.error('Failed to create informed consent PDF', error);
      showNotification({ color: 'red', message: 'Unable to prepare consent PDF. Please try again.' });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} size="lg" centered withCloseButton={viewOnly} closeOnClickOutside={false}>
      <Box px="md" py="sm">
        <Title order={3} mb="md">
          Informed Consent
        </Title>

        <Text size="sm" c="dimmed" mb="lg">
          Signing this form is voluntary. Your decision to use our services does not depend on signing this form. You
          may revoke your consent at any time by emailing us at support@polarissleep.com. Learn more about Polaris
          Sleep&apos;s Privacy Policy to understand how your data is shared under our information sharing agreement.
        </Text>

        {isMinor && (
          <Text size="sm" c="orange" mb="md">
            This patient is under 18. A parent or legal guardian must review and sign this consent on their behalf.
          </Text>
        )}

        <Select
          label="Relationship to patient"
          placeholder="Select relationship"
          data={['Self', 'Legal guardian']}
          value={relationship}
          onChange={(value) => setRelationship((value as ConsentRelationship) ?? 'Self')}
          error={requiresGuardian ? 'A minor requires a parent or legal guardian to sign.' : undefined}
          required
          mb="md"
        />

        <Box
          ref={agreementRef}
          p="md"
          mb="lg"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          <Stack gap="sm">
            <Text size="sm">
              Welcome! Polaris Sleep, P.C. referred to in this document "us," "we," "our," " or "Practice") is a
              professional corporation that hires and/or contracts with healthcare practitioners to deliver professional
              services related to sleep studies and the treatment of sleep disorders. Provider Group provides clinical
              services through a team-based care approach. All care teams consist of multiple licensed medical
              practitioners ("Providers"), and in any given virtual visit, you may interact with one or more Providers.
            </Text>
            <Text size="sm">
              We have partnered with HWX Matterhorn, LLC (referred to herein as "Polaris Sleep") which, among other
              things, provides the telehealth technology that enables the Practice to arrange for virtual visits and
              at-home sleep studies.
            </Text>
            <Text size="sm">
              Our services are specific to sleep studies and the treatment of sleep disorders, We are not your primary
              care physician. Responsibility for your overall medical care should remain with your primary care doctor,
              if you have one, and we encourage you to find one.
            </Text>

            <Text size="sm" fw={700}>
              Benefits and Risks of Telemedicine; Alternative Treatment
            </Text>
            <Text size="sm">
              There are risks, benefits, alternatives, and certain limitations of using telehealth to receive medical
              care. Telehealth may allow you to receive health care services in the convenience of your home. There are
              also potential risks and limitations to telemedicine. For example, your doctor could find the information
              you transmitted is insufficient to make a medical decision, meaning you will have to go in-person to a
              doctor, or that despite our strong security measures, your information could be breached by a bad actor.
              Also, because your doctor cannot physically examine you in-person, there is a chance that certain
              diagnoses or issues could be missed and risk causing you significant bodily harm, or death.
            </Text>
            <Text size="sm" fs="italic">
              Expected Benefits of Telehealth
            </Text>
            <List size="sm" withPadding>
              <List.Item>
                Improved access to care by enabling you to remain in your home while the Provider obtains a medical
                history and orders labs, imaging, external referrals, or other medical workup at distant/other sites.
              </List.Item>
              <List.Item>More efficient care evaluation and management.</List.Item>
            </List>
            <Text size="sm" fs="italic">
              Possible Risks of Receiving Treatment via Telehealth
            </Text>
            <List size="sm" withPadding>
              <List.Item>
                Delays in evaluation and treatment could occur due to deficiencies or failures of the equipment and
                technologies.
              </List.Item>
              <List.Item>
                The inability to conduct an in-person physical examination could result in misdiagnosis or delays
                inadequate care.
              </List.Item>
              <List.Item>
                The Provider may determine that the transmitted information is of inadequate quality, thus necessitating
                a rescheduled telehealth consult or a meeting with a local in-person doctor.
              </List.Item>
              <List.Item>
                A lack of access to complete medical records may result in adverse drug interactions or allergic
                reactions or other errors.
              </List.Item>
              <List.Item>
                Security protocols could fail, causing a breach of privacy or personal medical information including but
                not limited to your contact information, protected health information, or mental health information.
              </List.Item>
            </List>
            <Text size="sm">
              There are alternatives to telemedicine enabled services, including providers that deliver services in
              their practice offices. If you request, we can provide you with information related to an alternative
              provider.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Consent for Medical Treatment
            </Text>
            <Text size="sm">
              By indicating your agreement to this Informed Consent, you are consenting to receive medical care and
              treatment provided through the Provider Group. This includes your consent for all medical services
              provided under the general or specific instructions of a health care provider responsible for my care,
              including treatment by nurse practitioner or physician assistant, or other non-physician Providers or
              their designees under the direction and supervision of a physician.
            </Text>
            <Text size="sm">
              You understand that the practice of medicine is not an exact science and acknowledge that no guarantees
              have been made about the treatments, examinations or other services that you may receive. You have the
              right to discuss any treatment plan issued by Provider Group with any other health care provider.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Consent to Assignment of Benefits and Payment for Services
            </Text>
            <Text size="sm">
              You request that payment of and hereby assign payment of authorized medical benefits be made on your
              behalf directly to Provider Group for medical care and treatment furnished to you. You authorize Provider
              Group to release any medical information to your health insurance carrier, government-sponsored health
              care programs, and/or other third-party payors that is necessary to collect payments, process related
              health insurance claims, and/or to verify plan benefits in accordance with the Health Insurance
              Portability and Accountability Act ("HIPAA") standards and applicable state privacy laws. You authorize
              payment of service(s) otherwise payable to you under the terms of your health coverage directly to
              Provider Group and authorize Provider Group to be considered your authorized representative with respect
              to all payments and/or claims related to medical care and treatments furnished to you by Provider Group.
            </Text>
            <Text size="sm">
              If the medical benefits are not assignable to Provider Group or if you receive payments from any
              third-party payor for services rendered on your behalf by the Provider Group, you will forward those
              payments to the Provider Group upon receipt.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Financial Responsibility; Cancellation and No-Show Policy
            </Text>
            <Text size="sm">
              If services are not covered or covered charges are not paid in full including, but not limited to, any
              co-payment, co-insurance and/or deductible, or charges that are not covered by any third-party payor, you
              agree to pay for those services in accordance with our standard policies (including our financial
              assistance policy).
            </Text>
            <Text size="sm">
              You acknowledge that you are financially responsible for all charges for services rendered, regardless of
              insurance coverage. You agree to provide accurate and current insurance and demographic information and to
              promptly notify the Provider Group of any changes. You understand that benefits quoted by Provider Group
              or its billing agents are not a guarantee of payment by your insurer, and that you remain responsible for
              any amounts not paid by your insurer, including non-covered services, denied claims, deductibles,
              co-payments, and co-insurance.
            </Text>
            <Text size="sm">
              You agree to provide at least 24 hours&apos; advance notice to cancel or reschedule a scheduled
              appointment. Failure to provide timely notice or failure to appear for a scheduled appointment may result
              in a cancellation or no-show fee in accordance with Provider Group&apos;s then-current fee schedule, which
              fee is your sole responsibility and is not reimbursable by insurance.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Consent to Participate in Telehealth Consultation
            </Text>

            <Text size="sm" fw={700} td="underline">
              Consent to Electronic Communications and Electronic Signature
            </Text>
            <Text size="sm">
              By accepting this Informed Consent, you (i) consent to receive this Informed Consent, the Notice of
              Privacy Practices, and all other notices, disclosures, agreements, authorizations, and records relating to
              your care in electronic form, (ii) agree that your electronic acceptance constitutes your signature with
              the same legal effect as a handwritten signature, and (iii) consent to receive communications from Polaris
              Sleep and Provider Group (including their respective agents and service providers) by email, text/SMS
              message, push notification, in-app message, and automated or pre-recorded telephone call at the email
              address and telephone number(s) you have provided, including for appointment reminders, clinical
              follow-up, billing, account, device, and remote monitoring purposes. Message and data rates may apply. You
              may opt out of non-clinical text messages at any time by replying STOP, but withdrawing consent to
              clinical communications may affect Provider Group&apos;s ability to provide care.
            </Text>
            <Text size="sm" fw={700}>
              THE PLATFORM IS NOT INTENDED FOR THE PROVISION OF CLINICAL DIAGNOSIS REQUIRING AN IN-PERSON EVALUATION AND
              YOU SHOULD NOT USE IT IF YOU NEED IMMEDIATE/URGENT CARE. THE PROVIDER GROUP DOES NOT PROVIDE EMERGENCY
              MEDICAL CONSULTATION OR PRESCRIBE ANY CONTROLLED SUBSTANCES.
            </Text>
            <Text size="sm" fw={700}>
              WHEN IN DOUBT, PLEASE CALL 911 OR CONTACT YOUR LOCAL IN-PERSON HEALTHCARE PROFESSIONALS IMMEDIATELY.
            </Text>
            <Text size="sm" fw={700}>
              DO NOT DISREGARD, AVOID, OR DELAY IN OBTAINING IN-PERSON CARE FROM YOUR DOCTOR OR OTHER QUALIFIED
              PROFESSIONAL BECAUSE OF INFORMATION OR ADVICE YOU RECEIVED THROUGH THIS PLATFORM.
            </Text>
            <Text size="sm" fw={700}>
              IF YOU ARE THINKING ABOUT SUICIDE OR IF YOU ARE CONSIDERING HARMING YOURSELF OR OTHERS OR IF YOU FEEL THAT
              ANY OTHER PERSON MAY BE IN ANY DANGER OR IF YOU HAVE ANY MEDICAL EMERGENCY, YOU MUST IMMEDIATELY CALL THE
              EMERGENCY SERVICE NUMBER (911 IN THE U.S.) AND NOTIFY THE RELEVANT AUTHORITIES. SEEK IMMEDIATE IN-PERSON
              ASSISTANCE.
            </Text>

            <Text size="sm" fs="italic">
              Telehealth Services
            </Text>
            <Text size="sm">
              Telehealth involves the use of audio, visual, or other electronic communications to enable medical
              practitioners at different locations to share individual patient medical information for the purpose of
              providing patient care. Telehealth services offered by the Provider Group may also include chart review,
              health information sharing, clinical lab ordering, and associated non-clinical services, such as patient
              education. The information you provide may be used for diagnosis, therapy, medical triage, external
              referrals, follow-up and/or patient education, and may include any combination of the following: (1)
              health records and test results; (2) interactive, asynchronous or synchronous chat, audio, and/or video
              communications; or (3) live two-way audio and video.
            </Text>

            <Text size="sm" fs="italic">
              Provider Group&apos;s Duties and Obligations
            </Text>
            <Text size="sm">
              Telehealth providers, such as the Provider Group have the same duties and obligations that apply to
              in-person healthcare providers, which may require the disclosure of your confidential information under
              certain situations, including, but not limited to:
            </Text>
            <List size="sm" withPadding type="ordered" style={{ listStyleType: 'lower-alpha' }}>
              <List.Item>A duty to warn if you may present a risk of serious harm to yourself.</List.Item>
              <List.Item>A duty to warn if you may present a risk of serious harm to others.</List.Item>
              <List.Item>
                An obligation to report to the appropriate state agency current child abuse or neglect, elderly abuse,
                or otherwise disabled individual abuse.
              </List.Item>
              <List.Item>
                An obligation to respond to a valid subpoena or court order seeking the disclosure of your records.
              </List.Item>
            </List>

            <Text size="sm" fs="italic">
              Medical Information and Records
            </Text>
            <Text size="sm">
              All laws applicable to medical records privacy and access apply to telehealth. Dissemination of any
              patient identifiable images or information from your telehealth visit require your consent. Our Notice of
              Privacy Practices describes how the Provider Group protects, uses, and discloses medical information about
              you and how you can get access to this information. All existing confidentiality protections under federal
              and state law apply to information used or disclosed during your telehealth visit.
            </Text>

            <Text size="sm" fs="italic">
              Rights
            </Text>
            <Text size="sm">
              You may withhold or withdraw your consent to a telehealth consultation at any time before and/or during
              the consult without affecting your right to future care or treatment or risking the loss or withdrawal of
              any program benefits to which you would otherwise be entitled.
            </Text>

            <Text size="sm" fs="italic">
              State Licensure and Patient Location
            </Text>
            <Text size="sm">
              Providers are licensed to practice in specified U.S. states only and may provide services to you only when
              you are physically located in a state in which the treating Provider is licensed. You agree to truthfully
              disclose your physical location at the time of each telehealth encounter and acknowledge that
              misrepresenting your location may render Provider Group unable to provide services to you and may result
              in termination of your care.
            </Text>

            <Text size="sm" fs="italic">
              Recording and Documentation of Telehealth Visits
            </Text>
            <Text size="sm">
              Provider Group will document each telehealth encounter in your medical record. Provider Group may, but is
              not required to, record (in audio, video, image, or transcript form) telehealth encounters and may use
              automated or AI-assisted tools to transcribe, summarize, or otherwise document encounters for treatment,
              payment, healthcare operations, quality improvement, and compliance purposes. You will be notified before
              any recording occurs, and you may decline recording without affecting your right to receive care. You
              agree not to record (in any form) any telehealth encounter, or any portion of an encounter with a
              Provider, without the Provider&apos;s prior written consent.
            </Text>

            <Text size="sm" fs="italic">
              Prescriptions
            </Text>
            <Text size="sm">
              Whether to prescribe any medication is solely within the medical judgment of your Provider, who is not
              obligated to prescribe any medication you may request. Provider Group does not prescribe controlled
              substances. If a medication is prescribed, it will be sent electronically to a pharmacy of your choosing
              or to a pharmacy with which Provider Group has a relationship; you are responsible for the cost of the
              medication and any associated dispensing or shipping fees, except to the extent covered by your insurance.
            </Text>

            <Text size="sm" fs="italic">
              Identity Verification; Authority to Consent
            </Text>
            <Text size="sm">
              You represent that (i) you are at least 18 years of age (or, if accepting on behalf of a minor or other
              individual, you are the parent, legal guardian, or otherwise legally authorized representative of that
              individual and have authority to consent to care on their behalf), (ii) the information you provide to
              Provider Group is true, accurate, and complete, and (iii) you will provide such identification or
              verification information as Provider Group may reasonably request to confirm your identity and eligibility
              to receive services.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Consent to Remote Patient Monitoring and Collection of Device Data
            </Text>
            <Text size="sm">
              As part of your care, the Practice may arrange for certain remote patient monitoring ("RPM") and remote
              therapeutic monitoring ("RTM") services, which involve the collection, transmission, storage, and clinical
              review of health and device data generated by hardware devices (including, without limitation, CPAP, APAP,
              BiPAP, and other positive airway pressure devices, pulse oximeters, wearables, smart rings, mattress or
              bed sensors, and other connected sensors), mobile applications, and integrated third-party platforms
              (collectively, "Devices"). By accepting this Informed Consent, you authorize the Practice and its service
              providers, including, but not limited to, Polaris Sleep and certain Device manufacturers to collect,
              receive, transmit, store, access, use, and disclose data from such Devices in connection with your care,
              as further described below.
            </Text>
            <Text size="sm">
              <Text span fs="italic">
                Categories of Device Data Collected.
              </Text>{' '}
              Device Data may include, without limitation: (i) physiologic and biometric data (e.g., respiratory rate,
              airflow, mask seal, leak rate, apnea-hypopnea index, oxygen saturation, heart rate and heart rate
              variability, sleep stages, body position, snoring, and movement); (ii) device usage and performance data
              (e.g., usage hours, settings, alarms, error codes, firmware version, serial number, battery status); (iii)
              location and connectivity data (e.g., approximate location, IP address, Wi-Fi/Bluetooth connection
              metadata, time zone); (iv) self-reported data you enter (e.g., symptoms, sleep diary, medication,
              lifestyle); and (v) account, identifier, and contact information. Some of this data, when combined with
              identifiers, constitutes "protected health information" under HIPAA.
            </Text>
            <Text size="sm">
              <Text span fs="italic">
                Use and Disclosure of Device Data.
              </Text>{' '}
              Device Data may be used and disclosed to: (i) provide, support, and improve your treatment, including
              titration of therapy, coaching, adherence monitoring, alerts, and clinical decision-making by your
              Providers; (ii) operate, monitor, troubleshoot, repair, configure, and improve the Devices and the
              platform; (iii) submit claims to and obtain payment from your health plan, including claims under
              applicable RPM and RTM billing codes (e.g., CPT 99453, 99454, 99457, 99458, 98975, 98976, 98977, 98980,
              98981) where eligible; (iv) conduct healthcare operations, quality improvement, training, and outcomes
              analysis; (v) comply with applicable law and respond to legal process; and (vi) generate de-identified or
              aggregated data, which Polaris Sleep may use and disclose for any lawful purpose, including research,
              product development, and commercialization, in each case in accordance with HIPAA&apos;s de-identification
              standard.
            </Text>
            <Text size="sm">
              <Text span fs="italic">
                Not for Emergencies; Not Continuously Monitored.
              </Text>{' '}
              RPM is intended to supplement, not replace, in-person care. Device Data is not monitored on a continuous,
              real-time basis and Provider Group cannot guarantee that any specific reading, alert, or change in your
              condition will be reviewed or acted upon within any particular timeframe. Devices may produce inaccurate
              readings, may fail to transmit, and may be affected by network, power, sensor, fit, or environmental
              conditions. You should not rely on the Devices or RPM services to detect, diagnose, prevent, or treat any
              acute or emergency condition.{' '}
              <Text span fw={700}>
                If you are experiencing a medical emergency, call 911 or go to the nearest emergency department.
              </Text>
            </Text>
            <Text size="sm">
              <Text span fs="italic">
                Patient Responsibilities.
              </Text>{' '}
              You agree to: (i) use each Device in accordance with the manufacturer&apos;s and Provider Group&apos;s
              instructions; (ii) keep the Device powered, charged, connected to the internet or paired application, and
              updated as required for data to be transmitted; (iii) not modify, disassemble, reverse engineer, share,
              sell, or allow unauthorized persons to use the Device; (iv) safeguard the Device against loss, theft, and
              damage; (v) promptly notify Provider Group of any malfunction, loss, theft, or change in user; and (vi) if
              a Device is provided to you on a loaned, leased, or as-a-service basis, return the Device in good working
              condition (reasonable wear and tear excepted) upon termination of services or upon request, and pay any
              applicable replacement or non-return fees.
            </Text>
            <Text size="sm">
              <Text span fs="italic">
                Third-Party Devices and Apps.
              </Text>{' '}
              If you elect to connect a third-party device, app, or account (e.g., a wearable, fitness tracker, or
              electronic health record) to the platform, you authorize the transfer of data between such third party and
              Polaris Sleep and Provider Group for the purposes described above. The third party&apos;s collection and
              use of your data outside of the platform is governed by that third party&apos;s terms and privacy policy,
              and Polaris Sleep and Provider Group are not responsible for such third party&apos;s practices.
            </Text>

            <Text size="sm" fw={700} td="underline">
              Notice of Privacy Practices
            </Text>
            <Text size="sm">
              The Practice maintains a Notice of Privacy Practices. The notice describes how your personal health
              information may be used and disclosed, how you can get access to this information, and other rights you
              may have with respect to your health information.
            </Text>
            <Text size="sm">
              <Text span fw={700}>
                Please review the Notice of Privacy Practices carefully.
              </Text>{' '}
              By signing this Informed Consent you acknowledge and agree that you have received and read our Notice of
              Privacy Practices.
            </Text>
            <Text size="sm">
              By signing below, you acknowledge that you have read and understand the content of this Informed Consent,
              including, but not limited to the following:
            </Text>
            <List size="sm" withPadding>
              <List.Item>You hereby consent to receiving the Services virtually via a telehealth platform.</List.Item>
              <List.Item>
                You have received and read the Notice of Privacy Practices and you understand that your personal health
                information may be used and disclosed for permitted purposes, including scheduling services and billing
                purposes, and that you have certain rights under applicable law.
              </List.Item>
              <List.Item>
                You may be responsible for certain out-of-pocket costs including copayments, coinsurance and
                deductibles.
              </List.Item>
              <List.Item>
                You understand there is a risk of technical failures during the Services beyond the control of the
                Practice for which the Practice shall not be responsible.
              </List.Item>
              <List.Item>
                You understand that you have the right to withhold or withdraw my consent to Services at any time,
                without affecting my right to future care or treatment.
              </List.Item>
              <List.Item>
                You understand that if you are experiencing a medical emergency, that you will be directed to dial 9-1-1
                immediately and the Practice is not able to connect me directly to any local emergency services.
              </List.Item>
              <List.Item>
                You understand that as with any health care service no results can be guaranteed or assured.
              </List.Item>
              <List.Item>This consent remains fully effective until you revoke it in writing.</List.Item>
            </List>
          </Stack>
        </Box>

        {viewOnly ? (
          <Stack gap="sm">
            {onDownload && signedSignature && (
              <Button fullWidth onClick={() => handleDownload().catch(() => undefined)}>
                Download PDF
              </Button>
            )}
            <Button fullWidth variant="default" onClick={onClose}>
              Close
            </Button>
          </Stack>
        ) : (
          <>
            <Text fw={600} size="sm" mb={4}>
              Consent to contact
            </Text>
            <Checkbox
              mb="lg"
              label="By checking this box, I consent to being contacted about Polaris Sleep's services by email and phone. I understand I can unsubscribe at any time."
              checked={consentChecked}
              onChange={(event) => setConsentChecked(event.currentTarget.checked)}
            />

            <TextInput
              label={
                relationship === 'Legal guardian' ? "Parent or legal guardian's full legal name" : 'Full legal name'
              }
              placeholder="Full legal name"
              required
              mb="xl"
              value={signature}
              onChange={(event) => setSignature(event.currentTarget.value)}
            />

            <Button fullWidth disabled={!canSubmit} onClick={handleContinue}>
              {submitting ? 'Submitting...' : 'Continue'}
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
}
